import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import loadTaxExemptStatus from "@SupplyLand/Store/Data/TaxExempt/Handlers/LoadTaxExemptStatus";
import { CustomState } from "@SupplyLand/Store/Data/TaxExempt/TaxExemptReducer";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const taxExemptStatus = state.taxExemptData.taxExemptStatus;
    return {
        taxExemptStatus,
    };
};

const mapDispatchToProps = {
    loadTaxExemptStatus,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface TaxExemptStatusStyles {
    taxStatusCardContainer?: CardContainerStyles;
    taxStatusCardHeading?: CardContainerStyles;
    exposureZoneGridItem?: GridItemProps;
    taxHeaderGridItem?: GridItemProps;
    cardContainer?: CardContainerStyles;
    linkText?: TypographyProps;
    labelText?: TypographyProps;
    valueText?: TypographyProps;
    gridContainer?: GridContainerProps;
}

export const taxExemptStatusStyles: TaxExemptStatusStyles = {
    linkText: {
        css: css`
            text-decoration: underline;
        `,
    },
    labelText: {
        css: css`
            font-weight: bold;
        `,
    },
    taxStatusCardHeading: {
        gridItem: {
            css: css`
                padding: 0 0 0 20px;
            `,
        },
        cardDivider: {
            css: css`
                border-bottom: 40px;
                padding-bottom: 40px;
                margin-bottom: 40px;
            `,
        },
    },

    taxHeaderGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0 0 0 20px;
            margin-top: 20px;
            background: black;
            color: white;
        `,
    },
    taxStatusCardContainer: {
        gridItem: {
            width: 12,
            css: css`
                margin: 20px;
                padding: 10px !important;
                flex-wrap: wrap !important;
                flex-direction: row;
            `,
        },
        cardDivider: {
            css: css`
                padding-bottom: 40px;
            `,
        },
    },
    exposureZoneGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            padding: 0;
            flex-wrap: wrap !important;
            flex-direction: column;
        `,
    },
    gridContainer: {
        offsetCss: css`
            padding-left: 15px;
        `,
    },
};

const styles = taxExemptStatusStyles;

class TaxExemptStatus extends React.Component<Props> {
    componentDidMount() {
        if (this.props.taxExemptStatus.certificates === undefined) {
            const { loadTaxExemptStatus } = this.props;
            loadTaxExemptStatus();
        }
    }

    componentDidUpdate() {
        if (this.props.taxExemptStatus.certificates === undefined) {
            const { loadTaxExemptStatus } = this.props;
            loadTaxExemptStatus();
        }
    }

    render() {
        const { taxExemptStatus } = this.props;

        return (
            <CardList
                css={css`
                    padding-bottom: 50px;
                `}
                data-test-selector="cardTaxExemptStatus"
            >
                <CardListHeading heading={translate("Tax Exempt Status")} />
                <CardContainer {...styles.taxStatusCardContainer}>
                    <Link href="/helpcenter/taxinformation">
                        <Typography {...styles.linkText}>{siteMessage("Add a tax certificate")}</Typography>
                    </Link>
                </CardContainer>
                {taxExemptStatus &&
                    taxExemptStatus.certificates &&
                    taxExemptStatus.certificates.map(certificates => (
                        <>
                            <GridItem width={12} {...styles.taxHeaderGridItem}>
                                <Typography {...styles.labelText}>
                                    {certificates.exposure_zone && certificates.exposure_zone.state
                                        ? certificates.exposure_zone.state.name
                                        : translate("Unknown state")}
                                </Typography>
                            </GridItem>
                            <CardContainer key={certificates.tax_number}>
                                <GridContainer {...styles.gridContainer}>
                                    <GridItem {...styles.exposureZoneGridItem}>
                                        <Typography {...styles.labelText}>{translate("Verified")}</Typography>
                                        <Typography {...styles.valueText}>
                                            {certificates.verified ? "Verified" : "Not verified"}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.exposureZoneGridItem}>
                                        <Typography {...styles.labelText}>{translate("Signed Date")}</Typography>
                                        <Typography {...styles.valueText}>{certificates.signed_date}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.exposureZoneGridItem}>
                                        <Typography {...styles.labelText}>{translate("Status")}</Typography>
                                        <Typography {...styles.valueText}>{certificates.status.name}</Typography>
                                    </GridItem>
                                    <GridItem {...styles.exposureZoneGridItem}>
                                        <Typography {...styles.labelText}>{translate("Expires")}</Typography>
                                        <Typography {...styles.valueText}>{certificates.expiration_date}</Typography>
                                    </GridItem>
                                </GridContainer>
                            </CardContainer>
                        </>
                    ))}
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(TaxExemptStatus),
    definition: {
        displayName: "Tax Exempt Status",
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
